import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sequence} from '../../../../model/sequence';
import {Tenant} from '../../../../model/tenant';
import {Organization} from '../../../../model/organization';
import {TenantsService} from '../../../../services/tenants.service';
import {AuthGuard} from '../../../../common/auth-guard.service';
import {LoggedUser, User} from '../../../../common/logged-user.service';
import {SequencePersistRequest} from '../../../../model/requests/sequence-persist-request';
import {AppConfig} from '../../../../shared/global-variables';
import {noSpecialCharacters, noWhitespaces} from '../../../../shared/custom-validators';
import {PreviousRouteService} from '../../../../services/previous-route.service';

@Component({
  selector: 'app-sequence-form-info',
  templateUrl: './sequence-form-info.component.html',
  styleUrls: ['./sequence-form-info.component.scss']
})
export class SequenceFormInfoComponent {
  @Input() mode: 'create' | 'edit';
  @Input() sequence: Sequence;
  @Input() tenants: Tenant[];
  @Input() formLoaded: boolean;
  organizations: Organization[];

  @Output() sequencePersistRequestChange: EventEmitter<SequencePersistRequest> = new EventEmitter<SequencePersistRequest>();
  @Output() sequenceChange: EventEmitter<Sequence> = new EventEmitter<Sequence>();
  @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
  @Output() onCancel: EventEmitter<string> = new EventEmitter<string>();

  public sequenceScheduleTypes: { label: string, value: string }[];

  public sequenceForm: FormGroup;
  public formSuccess: boolean;

  public persistingChanges: any;

  public currentDate: Date;

  public recurrentTimes = [{key: 1, value: 1}, {key: 2, value: 2}, {key: 3, value: 3},
    {key: 4, value: 4}, {key: 5, value: 5}, {key: 6, value: 6}, {key: 10, value: 10}, {key: 12, value: 12},
    {key: 15, value: 15}, {key: 20, value: 20}, {key: 30, value: 30}];

  constructor(private readonly formBuilder: FormBuilder,
              private readonly tenantsService: TenantsService,
              private readonly authGuard: AuthGuard,
              private readonly loggedUser: LoggedUser,
              private readonly previousService: PreviousRouteService,
              private readonly cdr: ChangeDetectorRef
  ) {
    this.sequenceScheduleTypes = [
      {label: 'misc.sequence.scheduleType.none', value: 'NONE'},
      {label: 'misc.sequence.scheduleType.recurrent', value: 'RECURRENT'},
      {label: 'misc.sequence.scheduleType.daily', value: 'SCHEDULED'}
    ];
    this.currentDate = new Date();
  }

  loadFormComponent(): void {
    this.organizations = [this.sequence?.organization];
    this.createForm();
    if (this.sequence?.tenant?.uuid) {
      if (!this.authGuard.superAdminRequired() && this.loggedUser.user.organizationUuid) {
        this.organizations = this.loggedUser.user.organizations;
        this.sequenceForm.get('organization').setValue(this.organizations[0]);
        this.sequenceForm.get('organization').disable();
      } else {
        this.loadData(this.sequence.tenant.uuid).then((response: { organizations: Organization[] }): void => {
          this.organizations = response.organizations;
          this.sequenceForm.get('organization').enable();
        });
      }

    } else {
      this.sequenceForm.get('organization').disable();
    }
  }

  onTenantChange(event: { value: Tenant }): void {
    if (event.value?.uuid) {
      this.sequenceForm.get('organization').setValue(null);
      this.loadData(event.value.uuid).then((response: { organizations: Organization[] }): void => {
        this.organizations = response.organizations;
        this.sequenceForm.get('organization').enable();
      });
    } else {
      this.sequenceForm.get('organization').disable();
    }
  }

  onScheduleTypeChange(event: { value: string }): void {
    this.cdr.detectChanges();
    switch (event.value) {
      case 'RECURRENT':
        this.switchValidators('startDate', false, [Validators.required]);
        this.switchValidators('endDate', false, [Validators.required]);
        this.switchValidators('recurrentTime', true, [Validators.required]);
        break;
      case 'SCHEDULED':
        this.switchValidators('startDate', true, [Validators.required]);
        this.switchValidators('endDate', true, [Validators.required]);
        this.switchValidators('recurrentTime', false, [Validators.required]);
        break;
    }
  }

  persistSequenceData(): SequencePersistRequest {
    if (!this.sequenceForm.valid) {
      return;
    }

    this.persistingChanges = true;

    return this.buildPersistData();
  }

  validateDatesForm(): void {
    const startDate = this.sequenceForm.get('startDate')?.value;
    const endDate = this.sequenceForm.get('endDate')?.value;

    if (endDate && startDate && endDate < startDate) {
      this.sequenceForm.get('endDate')?.setErrors({endDateLowerThanStartDate: true});
    } else if (this.sequenceForm.get('endDate').hasError('endDateLowerThanStartDate')) {
      delete this.sequenceForm.get('endDate').errors['endDateLowerThanStartDate'];
      this.sequenceForm.get('endDate').updateValueAndValidity();
    }
  }

  private createForm(): void {

    const controlsConfig: any = this.buildControlsConfigForm(this.sequence);

    this.sequenceForm = this.formBuilder.group(controlsConfig);
    this.sequenceForm.markAsPending();

    this.disableFixedFields();

    if (!this.authGuard.superAdminRequired()) {
      this.sequenceForm.get('tenant').disable();
      this.loggedUser.userObservable.subscribe((user: User): void => {
        if (user.organization) {
          this.tenantsService.getTenantOrganizationSummaries(user.tenant).subscribe(response => {
            this.organizations = response.organizations;
            this.sequenceForm.get('organization').setValue(this.organizations[0]);
          });
        } else {
          this.sequenceForm.get('organization').enable();
        }
      });
    }

    this.cdr.detectChanges();
  }

  private buildControlsConfigForm(sequence: Sequence): any {
    const getSequenceScheduleType = (): string | undefined => {
      let type: string;
      if (sequence.recurrentSequenceConfig) {
        type = 'RECURRENT';
      } else if (sequence.scheduledSequenceConfig) {
        type = 'SCHEDULED';
      } else {
        type = 'NONE';
      }
      return type;
    };

    const getScheduledTime = (): Date | undefined => {
      const date: Date = new Date();
      if (sequence.scheduledSequenceConfig?.startHour !== undefined && sequence.scheduledSequenceConfig?.startMinute !== undefined) {
        date.setHours(sequence.scheduledSequenceConfig.startHour, sequence.scheduledSequenceConfig.startMinute);
      }

      return date;
    };

    const getDay = (day: boolean | undefined): boolean => {
      return day ?? false;
    };

    return {
      name: [sequence.name, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      tenant: [sequence.tenant, [Validators.maxLength(255), Validators.required]],
      organization: [sequence.organization, [Validators.maxLength(255), Validators.required]],
      sequenceScheduleType: [getSequenceScheduleType(), [Validators.required]],
      recurrentTime: sequence?.recurrentSequenceConfig?.recurrentTime ?? 1,
      scheduledTime: getScheduledTime(),
      startDate: sequence?.scheduledSequenceConfig?.startDate ? new Date(sequence?.scheduledSequenceConfig?.startDate) : new Date(),
      endDate: [sequence?.scheduledSequenceConfig?.endDate ? new Date(sequence?.scheduledSequenceConfig?.endDate) : new Date(), Validators.required],
      monday: getDay(sequence?.scheduledSequenceConfig?.monday),
      tuesday: getDay(sequence?.scheduledSequenceConfig?.tuesday),
      wednesday: getDay(sequence?.scheduledSequenceConfig?.wednesday),
      thursday: getDay(sequence?.scheduledSequenceConfig?.thursday),
      friday: getDay(sequence?.scheduledSequenceConfig?.friday),
      saturday: getDay(sequence?.scheduledSequenceConfig?.saturday),
      sunday: getDay(sequence?.scheduledSequenceConfig?.sunday)
    };
  }

  private buildPersistData(): SequencePersistRequest {
    const formValue = this.sequenceForm.getRawValue();

    let recurrentSequenceConfig: any;
    let scheduledSequenceConfig: any;

    switch (formValue.sequenceScheduleType) {
      case 'RECURRENT':
        recurrentSequenceConfig = {recurrentTime: formValue.recurrentTime};
        break;
      case 'SCHEDULED':
        const scheduledTime: Date = formValue.scheduledTime;
        const startHour: number = scheduledTime.getHours();
        const startMinute: number = scheduledTime.getMinutes();

        formValue.startDate.setHours(0, 0, 0, 0);

        scheduledSequenceConfig = {
          startHour,
          startMinute,
          startDate: formValue.startDate.getTime(),
          endDate: formValue.endDate.getTime(),
          monday: formValue.monday,
          tuesday: formValue.tuesday,
          wednesday: formValue.wednesday,
          thursday: formValue.thursday,
          friday: formValue.friday,
          saturday: formValue.saturday,
          sunday: formValue.sunday
        };
        break;
    }

    this.sequence.name = formValue.name;
    this.sequence.tenant = formValue.tenant;
    this.sequence.organization = formValue.organization;
    this.sequence.recurrentSequenceConfig = recurrentSequenceConfig;
    this.sequence.scheduledSequenceConfig = scheduledSequenceConfig;

    return {
      name: formValue.name,
      tenantUuid: formValue.tenant.uuid,
      organizationUuid: formValue?.organization?.uuid,
      uuid: this.mode === 'edit' ? this.sequence.uuid : null,
      recurrentSequenceConfig,
      scheduledSequenceConfig,
      configuration: undefined
    };

  }

  private disableFixedFields(): void {
    if (this.mode === 'edit') {
      this.sequenceForm.get('tenant').disable();
    }
  }

  private loadData(tenantUuid: string): Promise<any> {
    return this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise();
  }

  private updateFormField(field: string, validatorsToRemove: any[], validatorsToAdd: any[]): void {
    if (validatorsToAdd.length > 0) {
      this.sequenceForm.get(field)?.addValidators(validatorsToAdd);
    }
    if (validatorsToRemove.length > 0) {
      this.sequenceForm.get(field)?.removeValidators(validatorsToRemove);
    }
    this.sequenceForm.get(field)?.updateValueAndValidity();
  }

  private switchValidators(field: string, activate: boolean, validators: any[]): void {
    if (!activate) {
      this.updateFormField(field, validators, []);
    } else {
      this.updateFormField(field, [], validators);
    }
  }

  onNextStep(): void {
    if (this.sequenceForm.valid) {
      this.sequenceChange.emit(this.sequence);
      this.sequencePersistRequestChange.emit(this.persistSequenceData());
      this.nextStep.emit();
    }
  }

  cancel(): void {
    this.onCancel.emit(this.previousService.getPreviousUrl());
  }

  isValid(): boolean {
    const isValid: boolean = this.sequenceForm?.controls?.sequenceScheduleType?.value === 'SCHEDULED' ?
        this.sequenceForm?.controls?.monday?.value ||
        this.sequenceForm?.controls?.tuesday?.value ||
        this.sequenceForm?.controls?.wednesday?.value ||
        this.sequenceForm?.controls?.thursday?.value ||
        this.sequenceForm?.controls?.friday?.value ||
        this.sequenceForm?.controls?.saturday?.value ||
        this.sequenceForm?.controls?.sunday?.value : true;

    return (!(this.sequenceForm?.valid && isValid) ?? false);
  }
}
